import { useEffect, useRef, useState } from 'react';
import { s } from './Pattern.styles';
import Select from 'react-select';
import {
  AppraisalPointData,
  MarketPriceEstimationData,
  NumberOfFailedBidData,
  OldAgeData,
  SalesDaysData,
} from '../data';
import { usePatternContext } from '../../../../context/PatternContext';
import { Pattern1, Pattern2 } from './data';
import OptionTooltip from './OptionTooltip';
import styled from 'styled-components';

interface Props {
  setSelectedTab: (tab: string) => void;
  savePattern: () => void;
  pattern1: Pattern1;
  pattern2: Pattern2;
  setPattern2: (pattern: Pattern2) => void;
  isEditMode?: boolean;
  upDatePattern: () => void;
}

const CorePattern = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const {
    setIsStep2Done,
    setSelectedTab,
    pattern1,
    pattern2,
    setPattern2,
    isEditMode,
  } = usePatternContext();

  const customStyles = {
    container: (provided: any) => ({
      ...provided,
      flex: 1,
    }),
    control: (provided: any) => ({
      ...provided,
      display: 'flex',
      flexDirection: 'row',
      borderRadius: '8px',
      border: 'none',
      background: '#F7F8FC',
      height: '40px',
      fontFamily: 'Spoqa Han Sans Neo',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '-2%',
      lineHeight: '145%',
      color: '#616161',
      width: '100%',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      order: 1,
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontFamily: 'Spoqa Han Sans Neo',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '-2%',
      lineHeight: '145%',
      backgroundColor: state.isSelected ? '#e6e6e6' : provided.backgroundColor,
      color: state.isSelected ? '#222222' : '#616161',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      paddingRight: 0, // 패딩을 없앰
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      whiteSpace: 'nowrap',
    }),
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showTooltip &&
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  const handleReset = () => {
    setPattern2({
      saleProfit: 'A',
      hiddenValue: 'A',
      takeProfit: 'N',
      marketPrice: '',
      appraisalPoint: '',
      bidCount: '',
      oldAge: '',
      saleDays: '',
      investmentAmount: '평균 실투자금',
      enteredAmount: '',
    });
  };

  const ChangeValueToLabel = (value: string, data: any) => {
    const newValue = data.find((item: any) => item.value === value)?.label;
    return newValue ?? '';
  };
  const [tooltips, setTooltips] = useState<{ [key: string]: boolean }>({
    tooltip1: false,
    tooltip2: false,
    tooltip3: false,
    tooltip4: false,
    tooltip5: false,
  });
  const handleTooltipToggle = (tooltipKey: string) => {
    setTooltips((prev) => ({
      ...prev,
      [tooltipKey]: !prev[tooltipKey],
    }));
  };

  return (
    <s.PatternBoxContainer>
      <s.TitleBox>
        <div>핵심 알고리즘</div>
        <div>
          <span>선택</span>
        </div>
      </s.TitleBox>
      <s.PatterBoxInner>
        <s.Column>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>
              <span> 매각차익</span>
              <img
                src="/assets/images/icon/info-16.svg"
                alt=""
                onClick={() => {
                  handleTooltipToggle('tooltip1');
                }}
                style={{ cursor: 'pointer' }}
              />
            </s.BlockLabel>
            {tooltips.tooltip1 && (
              <OptionTooltip
                text={[`매각차익=매각가-경매 낙찰가`]}
                setTooltip={() => handleTooltipToggle('tooltip1')}
              />
            )}
            <s.BlockContent
              style={{
                gap: '12px',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <s.RadioButtonLabel htmlFor="">
                <input
                  type="radio"
                  value="A"
                  checked={pattern2.saleProfit === 'A'}
                  onChange={(e) => {
                    setPattern2({
                      ...pattern2,
                      saleProfit: e.target.value,
                      takeProfit: 'Y',
                    });
                  }}
                />
                전체(무관)
              </s.RadioButtonLabel>
              <s.RadioButtonLabel htmlFor="">
                <input
                  type="radio"
                  value="Y"
                  checked={pattern2.saleProfit === `Y`}
                  onChange={(e) => {
                    setPattern2({
                      ...pattern2,
                      saleProfit: e.target.value,
                      takeProfit: 'Y',
                    });
                  }}
                />
                {pattern1.type === 'A'
                  ? `차익有(낙찰가<매각가)`
                  : `차익有(금융비<임대료)`}
              </s.RadioButtonLabel>
              <s.RadioButtonLabel htmlFor="">
                <input
                  type="radio"
                  value="N"
                  checked={pattern2.saleProfit === `N`}
                  onChange={(e) => {
                    setPattern2({
                      ...pattern2,
                      saleProfit: e.target.value,
                      takeProfit: '',
                    });
                  }}
                />
                {pattern1.type === 'A'
                  ? `차익無(낙찰가>매각가)`
                  : `차익無(금융비>임대료)`}
              </s.RadioButtonLabel>
            </s.BlockContent>
          </s.Block>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>
              {' '}
              <span>숨은가치</span>
              <img
                src="/assets/images/icon/info-16.svg"
                alt=""
                onClick={() => {
                  handleTooltipToggle('tooltip2');
                }}
                style={{ cursor: 'pointer' }}
              />
            </s.BlockLabel>
            {tooltips.tooltip2 && (
              <OptionTooltip
                text={[
                  `숨은가치=시세-감정가 \n 감정시점의 감정가와 최근 시세와의 차이가 발생할 수 있습니다. `,
                ]}
                setTooltip={() => handleTooltipToggle('tooltip2')}
              />
            )}
            <s.BlockContent
              style={{
                gap: '12px',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <s.RadioButtonLabel htmlFor="">
                <input
                  type="radio"
                  value="A"
                  checked={pattern2.hiddenValue === 'A'}
                  onChange={(e) => {
                    setPattern2({ ...pattern2, hiddenValue: e.target.value });
                  }}
                />
                전체(무관)
              </s.RadioButtonLabel>
              <s.RadioButtonLabel htmlFor="">
                <input
                  type="radio"
                  value="L"
                  checked={pattern2.hiddenValue === `L`}
                  onChange={(e) => {
                    setPattern2({ ...pattern2, hiddenValue: e.target.value });
                  }}
                />
                {`저평가 (시세>감정가)`}
              </s.RadioButtonLabel>
              <s.RadioButtonLabel htmlFor="">
                <input
                  type="radio"
                  value="H"
                  checked={pattern2.hiddenValue === `H`}
                  onChange={(e) => {
                    setPattern2({ ...pattern2, hiddenValue: e.target.value });
                  }}
                />
                {`고평가 (시세<감정가)`}
              </s.RadioButtonLabel>
            </s.BlockContent>
          </s.Block>
        </s.Column>
        <s.Column>
          {pattern1.type === 'A' ? (
            <s.Block style={{ gap: '8px' }}>
              <s.BlockLabel>
                {' '}
                <span> 수익실현</span>
                <img
                  src="/assets/images/icon/info-16.svg"
                  alt=""
                  onClick={() => {
                    handleTooltipToggle('tooltip3');
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </s.BlockLabel>
              {tooltips.tooltip3 && (
                <OptionTooltip
                  text={[
                    `수익실현=매각가-(경매 낙찰가+부대비용)
부대비용에는 부동산 유형별로 취등록세,
법무사비, 명도비, 인테리어비 등이 포함될
수 있습니다.`,
                  ]}
                  setTooltip={() => handleTooltipToggle('tooltip3')}
                />
              )}
              <s.BlockContent
                style={{
                  gap: '12px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <s.RadioButtonLabel htmlFor="">
                  <input
                    type="radio"
                    value="Y"
                    checked={pattern2.takeProfit === 'Y'}
                    onChange={(e) => {
                      setPattern2({ ...pattern2, takeProfit: e.target.value });
                    }}
                    disabled={pattern2.saleProfit === 'N'}
                  />
                  부대비용 포함
                </s.RadioButtonLabel>
                <s.RadioButtonLabel htmlFor="">
                  <input
                    type="radio"
                    value="N"
                    checked={pattern2.takeProfit === `N`}
                    onChange={(e) => {
                      setPattern2({ ...pattern2, takeProfit: e.target.value });
                    }}
                    disabled={pattern2.saleProfit === 'N'}
                  />
                  {`부대비용 미포함`}
                </s.RadioButtonLabel>
              </s.BlockContent>
            </s.Block>
          ) : (
            ''
          )}
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>
              {' '}
              <span> 시세추정</span>
              <img
                src="/assets/images/icon/info-16.svg"
                alt=""
                onClick={() => {
                  handleTooltipToggle('tooltip4');
                }}
                style={{ cursor: 'pointer' }}
              />
            </s.BlockLabel>
            {tooltips.tooltip4 && (
              <OptionTooltip
                text={[
                  `주변 사례 및 최근 시세를 반영하여 \n AI(인공지능)을 통해 추정합니다.`,
                ]}
                setTooltip={() => handleTooltipToggle('tooltip4')}
              />
            )}
            <s.BlockContent>
              <div style={{ width: '100%' }}>
                <Select
                  options={MarketPriceEstimationData}
                  menuPortalTarget={document.body}
                  styles={customStyles}
                  placeholder="선택"
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  value={
                    pattern2.marketPrice === 'A'
                      ? null
                      : MarketPriceEstimationData.find(
                          (option) => option.value === pattern2.marketPrice,
                        )
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      setPattern2({
                        ...pattern2,
                        marketPrice: selectedOption.value,
                      });
                    }
                  }}
                />
              </div>
            </s.BlockContent>
          </s.Block>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>감정시점</s.BlockLabel>
            <s.BlockContent>
              <div style={{ width: '100%' }}>
                <Select
                  options={AppraisalPointData}
                  menuPortalTarget={document.body}
                  styles={customStyles}
                  placeholder="선택"
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  value={
                    pattern2.appraisalPoint === ''
                      ? null
                      : AppraisalPointData.find(
                          (option) => option.value === pattern2.appraisalPoint,
                        )
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      const selected = selectedOption.value;
                      setPattern2({ ...pattern2, appraisalPoint: selected });
                    }
                  }}
                />
              </div>
            </s.BlockContent>
          </s.Block>
        </s.Column>
        <s.Column>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>유찰횟수</s.BlockLabel>
            <s.BlockContent>
              <div style={{ width: '100%' }}>
                <Select
                  options={NumberOfFailedBidData}
                  menuPortalTarget={document.body}
                  styles={customStyles}
                  placeholder="선택"
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  value={
                    pattern2.bidCount === ''
                      ? null
                      : NumberOfFailedBidData.find(
                          (option) => option.value === pattern2.bidCount,
                        )
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      const selected = selectedOption.value;
                      setPattern2({ ...pattern2, bidCount: selected });
                    }
                  }}
                />
              </div>
            </s.BlockContent>
          </s.Block>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>노후년도</s.BlockLabel>
            <s.BlockContent>
              <div style={{ width: '100%' }}>
                <Select
                  options={OldAgeData}
                  menuPortalTarget={document.body}
                  styles={customStyles}
                  placeholder="선택"
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  value={
                    pattern2.oldAge === ''
                      ? null
                      : OldAgeData.find(
                          (option) => option.value === pattern2.oldAge,
                        )
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      const selected = selectedOption.value;
                      setPattern2({ ...pattern2, oldAge: selected });
                    }
                  }}
                />
              </div>
            </s.BlockContent>
          </s.Block>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>
              <span>매각일수</span>
              <img
                src="/assets/images/icon/info-16.svg"
                alt=""
                onClick={() => {
                  handleTooltipToggle('tooltip5');
                }}
                style={{ cursor: 'pointer' }}
              />
            </s.BlockLabel>
            {tooltips.tooltip5 && (
              <OptionTooltip
                text={['매각일수=매각일-경매 낙찰일']}
                setTooltip={() => handleTooltipToggle('tooltip5')}
              />
            )}
            <s.BlockContent>
              <div style={{ width: '100%' }}>
                <Select
                  options={SalesDaysData}
                  menuPortalTarget={document.body}
                  styles={customStyles}
                  placeholder="선택"
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  value={
                    pattern2.saleDays === ''
                      ? null
                      : SalesDaysData.find(
                          (option) => option.value === pattern2.saleDays,
                        )
                  }
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      const selected = selectedOption.value;
                      setPattern2({ ...pattern2, saleDays: selected });
                    }
                  }}
                />
              </div>
            </s.BlockContent>
          </s.Block>
        </s.Column>
        <s.Column>
          <s.Block style={{ gap: '8px' }}>
            <s.BlockLabel>실투자금</s.BlockLabel>
            <s.BlockContent
              style={{
                gap: '12px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <div
                style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}
              >
                <s.RadioButtonLabel htmlFor="">
                  <input
                    type="radio"
                    value="평균 실투자금"
                    checked={pattern2.investmentAmount === '평균 실투자금'}
                    onChange={(e) => {
                      setPattern2({
                        ...pattern2,
                        investmentAmount: e.target.value,
                        enteredAmount: '',
                      });
                    }}
                  />
                  평균 실투자금
                </s.RadioButtonLabel>
                <img
                  src="/assets/images/icon/info-16.svg"
                  alt=""
                  onClick={() => {
                    setShowTooltip(true);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 16,
                  alignItems: 'center',
                }}
              >
                <s.RadioButtonLabel htmlFor="">
                  <input
                    type="radio"
                    value="직접입력"
                    checked={pattern2.investmentAmount === `직접입력`}
                    onChange={(e) => {
                      setPattern2({
                        ...pattern2,
                        investmentAmount: e.target.value,
                      });
                    }}
                  />
                  {`직접입력`}
                </s.RadioButtonLabel>
              </div>
              {pattern2.investmentAmount === `직접입력` && (
                <s.InvestmentAmountBox>
                  <s.InvestmentAmountInput>
                    <span>낙찰가의</span>
                    <input
                      type="text"
                      value={pattern2.enteredAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          setPattern2({
                            ...pattern2,
                            enteredAmount: value,
                          });
                        }
                      }}
                    />
                    <span>%</span>
                  </s.InvestmentAmountInput>
                </s.InvestmentAmountBox>
              )}
            </s.BlockContent>
            {showTooltip && (
              <s.TooltipBox style={{ left: 0 }} ref={tooltipRef}>
                <s.TooltipTitle>
                  <span>평균 실투자금</span>
                  <img
                    src="/assets/images/close/close-dark-gray.svg"
                    alt="툴팁 닫기"
                    onClick={() => setShowTooltip(false)}
                  />
                </s.TooltipTitle>
                <div>
                  대략적인 계산을 기반으로 한 예상 실투자금의 평균입니다.
                </div>
              </s.TooltipBox>
            )}
          </s.Block>
        </s.Column>
      </s.PatterBoxInner>
    </s.PatternBoxContainer>
  );
};

export default CorePattern;
